import type React from 'react'
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

export interface SearchableSelectOption {
  id: string
  value: string
  label: string
  leadingLabel?: string
  trailingLabel?: string
  secondaryLabel?: string
  groupLabel?: string
  searchKeywords?: string[]
}

interface SearchableSelectProps {
  id?: string
  name?: string
  selectedValue: string | null | undefined
  selectedLabelFallback?: string
  options: SearchableSelectOption[]
  onChange: (option: SearchableSelectOption) => void
  onClear?: () => void
  onInputValueChange?: (value: string) => void
  placeholder?: string
  className?: string
  inputClassName?: string
  disabled?: boolean
  isLoading?: boolean
  isError?: boolean
  loadingText?: string
  errorText?: string
  emptyText?: string
  filterOptions?: boolean
  clearButtonAriaLabel?: string
  clearButtonText?: string
  ariaLabel?: string
  ariaLabelledBy?: string
  ariaDescribedBy?: string
  ariaInvalid?: boolean
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  id,
  name,
  selectedValue,
  selectedLabelFallback,
  options,
  onChange,
  onClear,
  onInputValueChange,
  placeholder,
  className,
  inputClassName,
  disabled = false,
  isLoading = false,
  isError = false,
  loadingText = 'Loading...',
  errorText = 'Could not load options.',
  emptyText = 'No options found.',
  filterOptions = true,
  clearButtonAriaLabel = 'Clear selection',
  clearButtonText = 'x',
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  ariaInvalid,
}) => {
  const listboxId = useId()
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const optionRefs = useRef<(HTMLDivElement | null)[]>([])

  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [dropdownRect, setDropdownRect] = useState<DOMRect | null>(null)
  const [inputText, setInputText] = useState('')

  const selectedOption = useMemo(
    () => options.find((option) => option.value === selectedValue) ?? null,
    [options, selectedValue]
  )

  const committedLabel = selectedOption?.label ?? selectedLabelFallback ?? ''

  useEffect(() => {
    setInputText(committedLabel)
    onInputValueChange?.(committedLabel)
  }, [committedLabel, onInputValueChange])

  const filteredOptions = useMemo(() => {
    if (!filterOptions) return options

    const committed = committedLabel.trim().toLowerCase()
    const query = inputText.trim().toLowerCase() === committed ? '' : inputText.trim().toLowerCase()
    if (!query) return options

    return options.filter((option) => {
      const searchableParts = [
        option.label,
        option.leadingLabel,
        option.trailingLabel,
        option.secondaryLabel,
        ...(option.searchKeywords ?? []),
      ]
        .filter((part): part is string => Boolean(part))
        .map((part) => part.toLowerCase())

      return searchableParts.some((part) => part.includes(query))
    })
  }, [committedLabel, filterOptions, inputText, options])

  const groupedOptions = useMemo(() => {
    let index = 0
    const groups = new Map<string, Array<{ option: SearchableSelectOption; index: number }>>()

    filteredOptions.forEach((option) => {
      const groupLabel = option.groupLabel ?? ''
      if (!groups.has(groupLabel)) {
        groups.set(groupLabel, [])
      }

      groups.get(groupLabel)?.push({ option, index })
      index += 1
    })

    return Array.from(groups.entries()).map(([groupLabel, items]) => ({ groupLabel, items }))
  }, [filteredOptions])

  const updateDropdownRect = () => {
    if (inputRef.current) {
      setDropdownRect(inputRef.current.getBoundingClientRect())
    }
  }

  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setInputText(committedLabel)
        onInputValueChange?.(committedLabel)
      }
    }

    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [committedLabel, onInputValueChange])

  useEffect(() => {
    if (!isOpen) return

    const handleReposition = () => updateDropdownRect()
    window.addEventListener('scroll', handleReposition, true)
    window.addEventListener('resize', handleReposition)

    return () => {
      window.removeEventListener('scroll', handleReposition, true)
      window.removeEventListener('resize', handleReposition)
    }
  }, [isOpen])

  const handleFocus = () => {
    if (disabled) return
    updateDropdownRect()
    setIsOpen(true)
    const selectedIndex = filteredOptions.findIndex((option) => option.value === selectedValue)
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : filteredOptions.length > 0 ? 0 : -1)
    inputRef.current?.select()
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setInputText(value)
    onInputValueChange?.(value)
    setActiveIndex(-1)

    if (!isOpen) {
      setIsOpen(true)
    }
  }

  const selectOption = (option: SearchableSelectOption) => {
    onChange(option)
    setInputText(option.label)
    onInputValueChange?.(option.label)
    setIsOpen(false)
    setActiveIndex(-1)
  }

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation()
    onClear?.()
    setInputText('')
    onInputValueChange?.('')
    setIsOpen(false)
    setActiveIndex(-1)
    inputRef.current?.focus()
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault()
        setIsOpen(true)
        setActiveIndex(event.key === 'ArrowDown' ? 0 : filteredOptions.length - 1)
      }
      return
    }

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault()
        const next = Math.min(activeIndex + 1, filteredOptions.length - 1)
        setActiveIndex(next)
        optionRefs.current[next]?.scrollIntoView({ block: 'nearest' })
        break
      }
      case 'ArrowUp': {
        event.preventDefault()
        const prev = Math.max(activeIndex - 1, 0)
        setActiveIndex(prev)
        optionRefs.current[prev]?.scrollIntoView({ block: 'nearest' })
        break
      }
      case 'Enter': {
        event.preventDefault()
        const option = filteredOptions[activeIndex]
        if (option) {
          selectOption(option)
        }
        break
      }
      case 'Home': {
        event.preventDefault()
        if (filteredOptions.length > 0) {
          setActiveIndex(0)
          optionRefs.current[0]?.scrollIntoView({ block: 'nearest' })
        }
        break
      }
      case 'End': {
        event.preventDefault()
        const lastIndex = filteredOptions.length - 1
        if (lastIndex >= 0) {
          setActiveIndex(lastIndex)
          optionRefs.current[lastIndex]?.scrollIntoView({ block: 'nearest' })
        }
        break
      }
      case 'Escape': {
        setIsOpen(false)
        setInputText(committedLabel)
        onInputValueChange?.(committedLabel)
        break
      }
    }
  }

  const showDropdown = isOpen && dropdownRect != null && !disabled
  const showClearButton = Boolean(onClear && inputText && !disabled)

  const dropdown = showDropdown
    ? createPortal(
        <div
          id={listboxId}
          role="listbox"
          style={{
            position: 'fixed',
            top: dropdownRect.bottom + 2,
            left: dropdownRect.left,
            width: dropdownRect.width,
            zIndex: 9999,
          }}
          aria-busy={isLoading}
          className="max-h-56 overflow-auto rounded border border-border-gray bg-white py-1 text-xs shadow-lg"
        >
          {isLoading && (
            <div role="status" className="px-3 py-2 text-text-gray-500">
              {loadingText}
            </div>
          )}
          {!isLoading && isError && (
            <div role="alert" className="px-3 py-2 text-danger">
              {errorText}
            </div>
          )}
          {!isLoading && !isError && filteredOptions.length === 0 && (
            <div role="status" className="px-3 py-2 text-text-gray-500">
              {emptyText}
            </div>
          )}
          {!isLoading &&
            !isError &&
            groupedOptions.map((group) => (
              <div
                key={group.groupLabel || '__default_group__'}
                role={group.groupLabel ? 'group' : undefined}
                aria-label={group.groupLabel || undefined}
              >
                {group.groupLabel && (
                  <div className="px-3 pt-2 pb-1 text-xs font-semibold tracking-wide text-text-gray-500 uppercase">
                    {group.groupLabel}
                  </div>
                )}
                <div>
                  {group.items.map(({ option, index }) => (
                    <div
                      key={option.id}
                      id={`${listboxId}-option-${index}`}
                      ref={(element) => {
                        optionRefs.current[index] = element
                      }}
                      role="option"
                      tabIndex={-1}
                      aria-selected={option.value === selectedValue}
                      onMouseDown={(event) => {
                        event.preventDefault()
                        selectOption(option)
                      }}
                      onMouseEnter={() => setActiveIndex(index)}
                      className={`cursor-pointer px-3 py-1.5 ${
                        index === activeIndex
                          ? 'bg-primary font-semibold text-white'
                          : 'text-gray-800 hover:bg-gray-100'
                      }`}
                    >
                      {option.trailingLabel ? (
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0 truncate">
                            {option.leadingLabel ?? option.label}
                          </div>
                          <div className="shrink-0 font-semibold">{option.trailingLabel}</div>
                        </div>
                      ) : (
                        <>
                          <div className="truncate">{option.leadingLabel ?? option.label}</div>
                          {option.secondaryLabel && (
                            <div
                              className={`truncate text-xs ${
                                index === activeIndex ? 'text-white' : 'text-text-gray-500'
                              }`}
                            >
                              {option.secondaryLabel}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>,
        document.body
      )
    : null

  return (
    <div ref={containerRef} className={`relative w-full ${className ?? ''}`}>
      {name && <input type="hidden" name={name} value={selectedValue ?? ''} />}
      <input
        id={id}
        ref={inputRef}
        type="text"
        role="combobox"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid || undefined}
        aria-expanded={showDropdown}
        aria-autocomplete="list"
        aria-controls={showDropdown ? listboxId : undefined}
        aria-activedescendant={activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined}
        value={inputText}
        onFocus={handleFocus}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        disabled={disabled}
        className={`w-full rounded border border-border-gray bg-white px-2 py-1.5 text-sm text-gray-900 disabled:cursor-not-allowed disabled:opacity-70 ${inputClassName ?? ''} ${
          showClearButton ? 'pr-6' : 'pr-2'
        }`}
      />

      {showClearButton && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute inset-y-0 right-1 flex min-w-6 items-center justify-center rounded px-1 text-xs text-text-gray-500 hover:text-text-gray-700"
          aria-label={clearButtonAriaLabel}
        >
          {clearButtonText}
        </button>
      )}

      {dropdown}
    </div>
  )
}
